import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  Modal,
  Select,
  TextField,
  Checkbox,
  Text,
  BlockStack,
} from '@shopify/polaris';

export function ScheduleModal({ 
  open, 
  onClose, 
  onSave, 
  serviceName = 'general',
  currentSchedule = null,
  isLoading = false
}) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tz = user?.timezone || currentWorkspace?.config?.timezone || detectedTz || 'UTC';
  const frequencyOptions = [
    { label: 'Every 15 minutes', value: '*/15' },
    { label: 'Every 30 minutes', value: '*/30' },
    { label: 'Every hour', value: 'hourly' },
    { label: 'Every 2 hours', value: '2h' },
    { label: 'Every 4 hours', value: '4h' },
    { label: 'Every 6 hours', value: '6h' },
    { label: 'Every 12 hours', value: '12h' },
    { label: 'Once daily', value: 'daily' },
    { label: 'Custom cron expression', value: 'custom' },
  ];

  const [frequency, setFrequency] = useState('daily');
  const [startTime, setStartTime] = useState('02:00');
  const [endTime, setEndTime] = useState('');
  const [customCron, setCustomCron] = useState('');
  const [days, setDays] = useState({
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: false,
    Sunday: false,
  });
  const [retryOnFailure, setRetryOnFailure] = useState(true);
  const [maxRetries, setMaxRetries] = useState(3);

  // Initialize form with current schedule data
  useEffect(() => {
    if (currentSchedule && open) {
      // Set frequency based on stored data
      if (currentSchedule.metadata?.frequency) {
        // Map human-readable frequency to frontend frequency values
        const frequencyMap = {
          'Every 15 minutes': '*/15',
          'Every 30 minutes': '*/30',
          'Every hour': 'hourly',
          'Every 2 hours': '2h',
          'Every 4 hours': '4h',
          'Every 6 hours': '6h',
          'Every 12 hours': '12h',
          'Once daily': 'daily',
          'Custom schedule': 'custom'
        };
        
        const mappedFrequency = frequencyMap[currentSchedule.metadata.frequency] || 'daily';
        setFrequency(mappedFrequency);
      } else if (currentSchedule.cronExpression) {
        setCustomCron(currentSchedule.cronExpression);
        setFrequency('custom');
      }
      
      // Set start time
      if (currentSchedule.metadata?.startTime) {
        setStartTime(currentSchedule.metadata.startTime);
      } else if (currentSchedule.metadata?.time) {
        setStartTime(currentSchedule.metadata.time);
      }
      
      // Set end time
      if (currentSchedule.metadata?.endTime) {
        setEndTime(currentSchedule.metadata.endTime);
      }
      if (currentSchedule.metadata?.days) {
        const newDays = { ...days };
        // Reset all days to false first
        Object.keys(newDays).forEach(day => {
          newDays[day] = false;
        });
        // Set only the stored days to true
        currentSchedule.metadata.days.forEach(day => {
          if (newDays.hasOwnProperty(day)) {
            newDays[day] = true;
          }
        });
        setDays(newDays);
      }
      
      if (currentSchedule.metadata?.retryOnFailure !== undefined) {
        setRetryOnFailure(currentSchedule.metadata.retryOnFailure);
      } else {
        setRetryOnFailure(true); // Default if not specified
      }
      if (currentSchedule.metadata?.maxRetries !== undefined) {
        setMaxRetries(currentSchedule.metadata.maxRetries);
      } else {
        setMaxRetries(3); // Default if not specified
      }
    } else if (open) {
      // Reset to defaults when opening for new schedule
      setFrequency('daily');
      setStartTime('02:00');
      setEndTime('');
      setCustomCron('');
      setDays({
        Monday: true,
        Tuesday: true,
        Wednesday: true,
        Thursday: true,
        Friday: true,
        Saturday: false,
        Sunday: false,
      });
      setRetryOnFailure(true);
      setMaxRetries(3);
    }
  }, [open, currentSchedule]);

  const handleDayChange = (day) => {
    setDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const handleSave = () => {
    const selectedDays = Object.keys(days).filter((day) => days[day]);
    
    if (selectedDays.length === 0) {
      alert('Please select at least one day');
      return;
    }

    if (frequency === 'custom' && !customCron.trim()) {
      alert('Please enter a valid cron expression');
      return;
    }

    const payload = {
      cron: frequency === 'custom' ? customCron : generateCronExpression(frequency, startTime, selectedDays),
      active: true,
      human: {
        frequency: getFrequencyDescription(frequency, startTime, selectedDays),
        startTime,
        endTime: endTime || undefined,
        days: selectedDays,
        type: frequency === 'custom' ? 'custom' : 'standard',
        retryOnFailure: retryOnFailure,
        maxRetries: maxRetries,
        timezone: tz
      }
    };

    onSave && onSave(payload);
    onClose();
  };

  const generateCronExpression = (freq, time, selectedDays) => {
    const [hour, minute] = time.split(':').map(Number);
    const dayOfWeek = selectedDays.map(day => {
      const dayMap = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 0 };
      return dayMap[day];
    }).sort().join(',');
    
    if (freq === '*/15') return `${minute} */15 * * ${dayOfWeek}`;
    if (freq === '*/30') return `${minute} */30 * * ${dayOfWeek}`;
    if (freq === 'hourly') return `${minute} * * * ${dayOfWeek}`;
    if (freq === '2h') return `${minute} */2 * * ${dayOfWeek}`;
    if (freq === '4h') return `${minute} */4 * * ${dayOfWeek}`;
    if (freq === '6h') return `${minute} */6 * * ${dayOfWeek}`;
    if (freq === '12h') return `${minute} */12 * * ${dayOfWeek}`;
    if (freq === 'daily') return `${minute} ${hour} * * ${dayOfWeek}`;
    
    return `${minute} ${hour} * * ${dayOfWeek}`;
  };

  const getFrequencyDescription = (freq, time, selectedDays) => {
    if (freq === '*/15') return 'Every 15 minutes';
    if (freq === '*/30') return 'Every 30 minutes';
    if (freq === 'hourly') return 'Every hour';
    if (freq === '2h') return 'Every 2 hours';
    if (freq === '4h') return 'Every 4 hours';
    if (freq === '6h') return 'Every 6 hours';
    if (freq === '12h') return 'Every 12 hours';
    if (freq === 'daily') return 'Once daily';
    if (freq === 'custom') return 'Custom schedule';
    
    return 'Custom schedule';
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Set Sync Schedule"
      primaryAction={{
        content: 'Save Schedule',
        onAction: handleSave,
        loading: isLoading,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="loose">
          <Select
            label="Frequency"
            options={frequencyOptions}
            value={frequency}
            onChange={setFrequency}
            helpText="Choose how often to run the sync"
          />
          
          {frequency === 'custom' && (
            <TextField
              label="Custom Cron Expression"
              value={customCron}
              onChange={setCustomCron}
              placeholder="0 2 * * 1-5"
              helpText="Enter a valid cron expression (e.g., 0 2 * * 1-5 for weekdays at 2 AM)"
            />
          )}
          
          <TextField
            label="Start Time"
            type="time"
            value={startTime}
            onChange={setStartTime}
            autoComplete="off"
            helpText="Time to start the sync (24-hour format)"
          />
          
          <TextField
            label="End Time (optional)"
            type="time"
            value={endTime}
            onChange={setEndTime}
            autoComplete="off"
            helpText="Optional: Time to stop accepting new sync requests"
          />
          
          <div>
            <Text variant="bodyMd" fontWeight="bold" style={{ marginBottom: '8px' }}>
              Run on days:
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.keys(days).map((day) => (
                <Checkbox
                  key={day}
                  label={day}
                  checked={days[day]}
                  onChange={() => handleDayChange(day)}
                />
              ))}
            </div>
          </div>
          
          <Checkbox
            label="Retry on failure"
            checked={retryOnFailure}
            onChange={setRetryOnFailure}
            helpText="Automatically retry failed sync operations"
          />
          
          {retryOnFailure && (
            <TextField
              label="Max Retries"
              type="number"
              value={maxRetries}
              onChange={setMaxRetries}
              min="1"
              max="10"
              autoComplete="off"
              helpText="Maximum number of retry attempts (1-10)"
            />
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

